import {
  USER_LOGIN_SUCCESS,
  MEDICAL_ROLES,
} from "../constants/userConstants";
import React, { useContext, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useHistory } from "react-router-dom";
import { Button, Menu, Tooltip, App, Modal, Alert } from 'antd';
import Joyride, { ACTIONS, EVENTS, LIFECYCLE, STATUS } from 'react-joyride';
import { MenuOutlined } from '@ant-design/icons';
import { logout } from "../actions/userActions";
import { changeUserPreferences } from "../services/userService";
import { ReactComponent as LogoRAVAIL } from '../media/logo_RAVAIL_horizontal.svg';
import { ReactComponent as LogoEasyVFC } from '../media/logo_EasyVFC.svg';
import { ReactComponent as LogoEasyVMC } from '../media/logo_EasyVMC.svg';
import { ReactComponent as LogoEasyVSPE } from '../media/logo_EasyVSPE.svg';
import { useAuth } from "react-oidc-context";
import { CLE_DECONNEXION } from "../utils/logoutBroadcast";

import axios from "axios";
import { AppContext } from "../AppContext";
import { useState } from "react";


function HeaderLinks() {
  const dispatch = useDispatch();
  let history = useHistory();
  const { message } = App.useApp();
  
  const [showWarn, setShowWarn] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const menuWrapperRef = useRef(null);
  const userLogin = useSelector((state) => state.userLogin);
  const { errorMessage, successMessage, infoMessage, warnMessage, userInfo } = userLogin;
  const auth = useAuth();
  const token = auth.user?.access_token;

  const { tutoState, setTutoState } = useContext(AppContext);

  useEffect(() => {
    if (errorMessage) {
      message.error(errorMessage);
    }
  }, [errorMessage]);

  
  useEffect(() => {
    if (successMessage) {
      message.success(successMessage);
    }
  }, [successMessage]);

  useEffect(() => {
    if (infoMessage) {
      message.info(infoMessage);
    }
  }, [infoMessage]);

  useEffect(() => {
    if (warnMessage) {
      setShowWarn(true);
    }
  }, [warnMessage]);

  useEffect(() => {
    if (token && userInfo && !userInfo.profession) {
      Modal.warning({
        title: "Aucun rôle associé à ce compte",
        centered: true,
        content: (
          <p>
            Votre compte n'a aucun rôle défini. Vous ne pouvez pas accéder aux fonctionnalités de RAVAIL.
            Veuillez contacter un administrateur pour qu'il vous assigne un rôle.
          </p>
        ),
        okText: "Compris",
      });
    }
  }, [userInfo]);

  const isMedical = (roles) => MEDICAL_ROLES.some(r => (roles ?? []).includes(r));
  const menuColor = (roles) => {
    const r = roles ?? [];
    if (r.includes('INFIRMIER')) return '#76e297';
    if (isMedical(r)) return '#04aceb';
    return '#f97575';
  };

  useEffect(() => {
    if (token && userInfo && isMedical(userInfo.roles) && !userInfo.disclaimerAccepted) {
      // On affiche un texte de responsabilité de l'utilisateur pour les médecins et infirmiers
      Modal.info({
        width:1010,
        title: "Responsabilité de l'utilisateur",
        centered: true,
        content: (
          <div>
            <p>
              Les outils RAVAIL intègrent des informations scientifiques issues de sources diverses : réglementation, littérature, recommandations de sociétés savantes, groupes de travail spécialisés, instances publiques, etc. Nous nous efforçons de tenir cette base de données à jour et ainsi d’effectuer des suggestions complexes basées sur les dernières données acquises de la science.
            </p>
            <p>
              Un soin particulier est porté pour citer les sources utilisées et les différents motifs aboutissant aux suggestions effectuées par l’outil. Cette volonté de transparence vise à aider l’utilisateur à tenir compte de leur niveau de fiabilité variable, dépendant des sources, ainsi que des éventuels biais introduits par les facteurs non considérés par nos algorithmes.
            </p>
            <p>
              Les professionnels de santé peuvent ainsi, grâce aux outils RAVAIL, utiliser des données issues d’analyses collectives pour faciliter et améliorer leurs évaluations individuelles à l’échelle d’une carrière complète.
            </p>
            <p>
              Toutefois, il appartient à l’utilisateur de certifier la pertinence des informations qu’il remet au salarié à l’issue d’une visite, grâce à ses propres compétences, à ses connaissances du poste de travail et de l'entreprise, aux documents à sa disposition ainsi qu’aux déclarations du travailleur concernant le travail réel effectué.
            </p>
            <p>
              RAVAIL ne saura être tenu responsable des informations erronées figurant dans les documents générés par son intermédiaire, puisqu’il n’a pas vocation à remplacer l’expertise humaine mais seulement à l’assister.
            </p>
            <p>
              En utilisant les outils RAVAIL, vous reconnaissez accepter cette responsabilité ainsi que nos <a href="/cgu" target="_blank" rel="noreferrer">conditions d'utilisation</a>.
            </p>
          </div>
        ),
        okText: "Je comprends et j'accepte cette responsabilité",
        onOk() {
          // On enregistre dans la base de données que l'utilisateur a accepté le disclaimer
          changeUserPreferences(token, undefined, undefined, true).then(() => {
            if(userInfo) userInfo.disclaimerAccepted = true;
            localStorage.setItem('userInfo', JSON.stringify(userInfo));
          })
          .catch((error) => {
            if(error === 401){
              dispatch(logout(auth));
            }
          });
          // Une fois le disclaimer accepté, on peut annoncer la nouvelle interface
          showNewInterfaceAnnouncement();
        }
      });
    }
  }, [userInfo]);

  const location = useLocation()

  // Popup d'annonce de la nouvelle interface (affichée une seule fois par navigateur)
  const showNewInterfaceAnnouncement = useCallback(() => {
    if (localStorage.getItem('newInterfaceAnnouncementSeen')) return;
    localStorage.setItem('newInterfaceAnnouncementSeen', "true");
    setShowAnnouncement(true);
  }, []);

  useEffect(() => {
    // À la connexion, on annonce la nouvelle interface
    if (token && userInfo) {
      const disclaimerPending = isMedical(userInfo.roles) && !userInfo.disclaimerAccepted;
      if (!disclaimerPending) {
        showNewInterfaceAnnouncement();
      }
    }
  }, [userInfo, token, showNewInterfaceAnnouncement]);

  useEffect(() => {
    const handleInvalidToken = e => {
      // Un autre onglet vient de démarrer une déconnexion : on purge l'état local tout de
      // suite. Le verrou (cf. utils/logoutBroadcast.js) empêche par ailleurs PrivateRoute,
      // App et LoginScreen de relancer une connexion tant qu'elle n'est pas terminée.
      // Sa suppression (fin de déconnexion) ne demande aucune action.
      if (e.key === CLE_DECONNEXION){
        if (e.oldValue === null && e.newValue) {
          dispatch(logout(auth));
        }
        return;
      }
      if (e.key === 'userInfo'){
        if(e.oldValue && !e.newValue){
          // On déconnecte l'utilisateur si la clé userInfo n'existe plus dans le localStorage (par exemple si l'utilisateur s'est déconnecté depuis un autre onglet)
          dispatch(logout(auth));
        }
        else{
          const data = JSON.parse(localStorage.getItem("userInfo"));
          dispatch({ type: USER_LOGIN_SUCCESS, payload: data });
        }
      }
    }
    window.addEventListener('storage', handleInvalidToken)
    return function cleanup() {
      window.removeEventListener('storage', handleInvalidToken)
    }
  }, [dispatch, auth]);

  useEffect(() => {
    // Une erreur de silent renew ne doit PAS déconnecter l'utilisateur : sur iOS/iPad,
    // l'ITP de WebKit bloque les iframes vers Keycloak et provoque de fausses erreurs.
    // automaticSilentRenew retentera via le refresh_token ; la déconnexion réelle reste
    // gérée par le flux normal (/logout, expiration, et le handler 'storage' ci-dessus).
    return auth.events.addSilentRenewError((error) => {
      console.warn("Silent renew error", error);
    })
  }, [auth.events]);


  const items = useMemo(() => {
    let items = [];
    if(!auth.isAuthenticated || (auth.isAuthenticated && !userInfo)){
      // On n'est pas connecté
      items.push(
        {
          key: "logo",
          label:
            <Link to="/" className="logoLink" style={{maxWidth: "40vw"}}>
              <AppContext.Consumer>
                {({outil}) => {
                  if(outil === "EasyVFC") return <LogoEasyVFC className="logoImg" style={{height: "140%"}}/>
                  else if(outil === "EasyVMC") return <LogoEasyVMC className="logoImg" style={{height: "140%"}}/>
                  else if(outil === "EasyVSPE") return <LogoEasyVSPE className="logoImg" style={{height: "140%"}}/>
                  else return <LogoRAVAIL className="logoImg"/>
                }}
              </AppContext.Consumer>
            </Link>,
          style: { marginRight: "auto" },
          className: "logoItem"
        },
        {
          key: "/",
          label:
            <Link to="/" >
              Accueil
            </Link>
        },
        {
          key: "/notre-solution",
          label:
            <Link to="/notre-solution">
              Notre solution
            </Link>
        },
        {
          key: "/qui-sommes-nous",
          label:
            <Link to="/qui-sommes-nous" >
              Qui sommes-nous ?
            </Link>
        },
        {
          key: "/blog",
          label:
            <Link to="/blog">
              Notre blog
            </Link>
        },
        {
          key: "/test-eligibilite-vfc",
          label:
            <Link to="/test-eligibilite-vfc">
              Test d'éligibilité VFC
            </Link>
        },
        {
          key: "/contact",
          label:
            <Link to="/contact">
              Nous contacter
            </Link>
        },
        {
          key: "/login",
          label:
            <Link to={{
              pathname: "/login" ,
              state: {
                from: location.pathname
              }
              }}>
              <Button
                type="primary"
                size="large"
                className="custom-btn-green"
              >
                Se connecter
              </Button>
            </Link>,
          className: "connexionItem"
        }
      );
    }
    else{
      // On est connecté
      if(isMedical(userInfo.roles)){
        items.push(
          {
            key: "logo",
            label:
              <Link to="/" className="logoLink" style={{maxWidth: "40vw"}}>
                <AppContext.Consumer>
                  {({outil}) => {
                    if(outil === "EasyVFC") return <LogoEasyVFC className="logoImg" style={{height: "140%"}}/>
                    else if(outil === "EasyVMC") return <LogoEasyVMC className="logoImg" style={{height: "140%"}}/>
                    else if(outil === "EasyVSPE") return <LogoEasyVSPE className="logoImg" style={{height: "140%"}}/>
                    else return <LogoRAVAIL className="logoImg"/>
                  }}
                </AppContext.Consumer>
              </Link>,
            style: { marginRight: "auto" },
            className: "logoItem"
          },
          {
            key: "/",
            label: 
              <Link to="/">
                Accueil
              </Link>
          },
          {
            key: "/visie",
            label:
              <Link to="/visie">
                <span style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#04aceb",
                }}>
                  VISI<span style={{color: "#76e297"}}>E</span>
                </span> (Visite Santé)
              </Link>
          },
          {
            key: "nom",
            label: 
              <div>
                <div style={{height: "32px", lineHeight: "36px", width: "fit-content", marginInline: "auto"}}>
                  {userInfo.firstName + " " + userInfo.lastName}
                </div>
                <Tooltip title={userInfo.profession ?? 'Rôle non défini'}>
                  <div style={{
                    lineHeight: "14px",
                    fontSize: "12px",
                    color: menuColor(userInfo.roles),
                    width: "fit-content",
                    maxWidth: "180px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    marginInline: "auto"
                  }}>
                    {userInfo.profession ?? 'Rôle non défini'}
                  </div>
                </Tooltip>
                {userInfo.expiredAt && new Date(userInfo.expiredAt) <= new Date() &&
                  <Tooltip
                    title={<>
                      <span>Votre compte a expiré, si ce service vous est utile </span>
                      <Link to="/contact">
                        <Button
                          type="primary"
                        >
                          prenez contact avec nous
                        </Button>
                      </Link>
                    </>}
                    styles={{root: {maxWidth: "min(100%, 700px)"}}}
                    placement="bottom"
                  >
                    <div className="daysBeforeExpiredOuter">
                      <div className="daysBeforeExpiredInner">
                        Compte expiré
                      </div>
                    </div>
                  </Tooltip>
                }
              </div>,
            className: "connectedItem",
            children: auth?.user?.profile?.resource_access?.["ravail_app"].roles.includes('SUPER_ADMIN') ? [
              {
                key: "/profile",
                label:
                  <Link to="/profile">
                    Mon profil
                  </Link>
              },
              {
                key: "/search",
                label:
                  <Link to="/search">
                    Rechercher une visite
                  </Link>
              },
              {
                key: "/register",
                label:
                  <Link to="/register">
                    Créer un nouvel utilisateur
                  </Link>
              },
              {
                key: "/newsletter",
                label:
                  <Link to="/newsletter">
                    Envoi email newsletter
                  </Link>
              },
              {
                key: "/db",
                label:
                  <Link to="/api/admin/db">
                    Modifier les matrices
                  </Link>
              },
              {
                key: "/logout",
                label:
                  <Link to="/logout">
                    Déconnexion
                  </Link>
              }
            ] : [
              {
                key: "/profile",
                label:
                  <Link to="/profile">
                    Mon profil
                  </Link>
              },
              {
                key: "/search",
                label:
                  <Link to="/search">
                    Rechercher une visite
                  </Link>
              },
              {
                key: "/logout",
                label:
                  <Link to="/logout">
                    Déconnexion
                  </Link>
              }
            ]
          }
        );
      }
      else {
        items.push(
          {
            key: "logo",
            label:
              <Link to="/" className="logoLink" style={{maxWidth: "40vw"}}>
                <AppContext.Consumer>
                  {({outil}) => {
                    if(outil === "EasyVFC") return <LogoEasyVFC className="logoImg" style={{height: "140%"}}/>
                    else if(outil === "EasyVMC") return <LogoEasyVMC className="logoImg" style={{height: "140%"}}/>
                    else if(outil === "EasyVSPE") return <LogoEasyVSPE className="logoImg" style={{height: "140%"}}/>
                    else return <LogoRAVAIL className="logoImg"/>
                  }}
                </AppContext.Consumer>
              </Link>,
            style: { marginRight: "auto" },
            className: "logoItem"
          },
          {
            key: "/",
            label: 
              <Link to="/">
                Accueil
              </Link>
          },
          {
            key: "/visie",
            label:
              <Link to="/visie">
                <span style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#04aceb",
                }}>
                  VISI<span style={{color: "#76e297"}}>E</span>
                </span> (Visite Santé)
              </Link>
          },
          {
            key: "nom",
            label: 
              <div>
                <div style={{height: "32px", lineHeight: "36px"}}>
                  {userInfo.firstName + " " + userInfo.lastName}
                </div>
                <Tooltip title={userInfo.profession ?? 'Rôle non défini'}>
                  <div style={{
                    lineHeight: "14px",
                    fontSize: "12px",
                    color: menuColor(userInfo.roles),
                    width: "fit-content",
                    maxWidth: "150px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    marginInline: "auto",
                    padding: "0px 8px"
                  }}>
                    {userInfo.profession ?? 'Rôle non défini'}
                  </div>
                </Tooltip>
              </div>,
            className: "connectedItem",
            children: [
              {
                key: "/profile",
                label:
                  <Link to="/profile">
                    Mon profil
                  </Link>
              },
              {
                key: "/logout",
                label:
                  <Link to="/logout">
                    Déconnexion
                  </Link>
              }
            ]
          }
        );
      }
    }
    return items;
  }, [userInfo, dispatch, auth.isAuthenticated, location]);

  // Accessibilité : rc-overflow (le moteur du menu Ant Design) conserve des copies
  // masquées des éléments qui débordent (aria-hidden="true", height: 0), mais celles-ci
  // contiennent toujours des liens/boutons focusables (ex. le bouton "Se connecter").
  // Lighthouse signale alors un arbre d'accessibilité mal formé. On retire ces copies
  // du parcours de tabulation et de l'arbre d'accessibilité via l'attribut `inert`.
  useEffect(() => {
    const wrapper = menuWrapperRef.current;
    if (!wrapper) return;

    const syncInert = () => {
      wrapper.querySelectorAll(".ant-menu-overflow-item").forEach((item) => {
        if (item.getAttribute("aria-hidden") === "true") {
          item.setAttribute("inert", "");
        } else {
          item.removeAttribute("inert");
        }
      });
    };

    syncInert();
    const observer = new MutationObserver(syncInert);
    observer.observe(wrapper, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["aria-hidden"],
    });
    return () => observer.disconnect();
  }, [items]);

  // DirtyFix: On ajoute un overlay custom pour le premier step du tutoriel (un bug fait que le premier step n'a pas d'overlay dans certains cas)
  useEffect(() => {
    if(tutoState.stepIndex === 0){ // On est sur le premier step
      if(userInfo?.recommendTuto){ // Le beacon doit s'afficher (on n'a pas encore fait le tutoriel)
        if(tutoState.run && tutoState.active){ // Le tutoriel vient de commencer
          // create the fallback overlay
          let overlay = document.createElement("div");
          overlay.class = "joyride-overlay-custom";
          overlay.id = "custom-overlay";
          overlay.style.cssText =
            "cursor: default; height: " + document.body.scrollHeight + "px; width: 100vw; pointer-events: auto; inset: 0px; overflow: hidden; position: absolute; z-index: 999; background-color: rgba(0, 0, 0, 0.5) !important; mix-blend-mode: hard-light;";
          
          document.body.appendChild(overlay);
        }
        else if(!tutoState.run && !tutoState.active && location.pathname === "/visie"){ // Le tutoriel vient d'être skipé
          // Si on a sauté le tutoriel alors qu'on nous le recommandait, on relance le tutoriel pour afficher le beacon
          // (uniquement sur la page d'accueil des outils, où se trouve le bouton du tutoriel)
          setTutoState(tutoState => {
            tutoState.steps[0].disableBeacon = false;
            return { ...tutoState, run: true, active: false, stepIndex: 0 };
          });
        }
      }
    }
    else{ // Le beacon ne doit pas affiché (on a déjà fait le tutoriel)
      let overlay = document.getElementById("custom-overlay");
      if(overlay) overlay.remove();
    }
  }, [tutoState.run, tutoState.active, tutoState.stepIndex]);

  const handleJoyrideCallback = useCallback((data) => {
    const { action, index, status, type } = data;
    if (STATUS.FINISHED === status) {
      // On enregistre dans la base de données que l'utilisateur a terminé le tutoriel
      changeUserPreferences(token, undefined, false);
      if(userInfo) userInfo.recommendTuto = false;
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
      // On met à jour le state du contexte
      setTutoState(tutoState => ({ ...tutoState, run: false, active: false, stepIndex: 0 }));
    }
    else if (STATUS.SKIPPED === status) {
      // On met à jour le state du contexte
      setTutoState(tutoState => ({ ...tutoState, run: false, active: false, stepIndex: 0 }));
      if(index === 0){ // On a skipé le premier step, on retire l'overlay custom
        let overlay = document.getElementById("custom-overlay");
        if(overlay) overlay.remove();
      }
    }
    else if (type === EVENTS.STEP_AFTER) {
      // Next step
      setTutoState({
        ...tutoState,
        active: true,
        stepIndex: index + (action === ACTIONS.PREV ? -1 : 1)
      });
    }
    else if (type === EVENTS.TARGET_NOT_FOUND) {
      // Next step
      setTutoState({
        ...tutoState,
        active: true,
        stepIndex: index + (action === ACTIONS.PREV ? -1 : 1)
      });
    }
  }, [setTutoState, tutoState, userInfo, token]);

  return (
    <div style={{paddingTop: "12px"}} >
      <Modal
        open={showAnnouncement}
        width={720}
        title="RAVAIL fait peau neuve ! 🎉"
        centered
        okText="Découvrir avec le tutoriel"
        cancelText="J'ai compris"
        onOk={() => {
          setShowAnnouncement(false);
          // On lance le tutoriel depuis la page d'accueil des outils
          setTutoState(tutoState => {
            tutoState.steps[0].disableBeacon = true;
            return { ...tutoState, run: true, active: true, stepIndex: 0 };
          });
          if (location.pathname !== "/visie") history.push("/visie");
        }}
        onCancel={() => setShowAnnouncement(false)}
      >
        <p>Une <b>nouvelle interface</b> est disponible pour vous simplifier le quotidien :</p>
        <ul>
          <li><b>Tous les types de visites</b> sont désormais pris en charge : embauche, périodique, mi-carrière, fin de carrière, suivi post-exposition...</li>
          <li>Les données sont <b>centralisées au niveau du salarié</b> : créez un nouveau salarié et retrouvez-y son identité, ses visites et son relevé de carrière.</li>
        </ul>
        <p>Vous pouvez (re)découvrir l'outil pas à pas grâce au tutoriel.</p>
      </Modal>
      {showWarn &&
        <Alert
          message={warnMessage}
          type="error"
          closable
          banner
          style={{fontWeight: 'bold'}}
          onClose={() => setShowWarn(false)}
        />
      }
      <div style={{ display: "flex" }} ref={menuWrapperRef}>
        <Menu
          overflowedIndicator={<MenuOutlined />}
          style={{background: "rgb(255 255 255 / 0%)", minWidth: 0, color: "#233551", flex:"auto"}}
          mode="horizontal"
          items={items}
          selectedKeys={[location.pathname]}
          subMenuCloseDelay={0}
          subMenuOpenDelay={0}
          triggerSubMenuAction="click"
        />
        {userInfo && isMedical(userInfo.roles) &&
          <Joyride
            continuous={true}
            disableOverlayClose={true}
            showSkipButton={true}
            hideCloseButton={true}
            hideBackButton={true}
            locale={{
              back: 'Précédent',
              close: 'Fermer',
              last: 'Terminer',
              next: 'Suivant',
              skip: 'Passer',
            }}
            callback={handleJoyrideCallback}
            run={tutoState.run}
            stepIndex={tutoState.stepIndex}
            steps={tutoState.steps}
            styles={
              {
                options: {
                  zIndex: 1000,
                  primaryColor: '#04aceb',
                },
                beaconInner: {
                  backgroundColor: '#e77477',
                },
                beaconOuter: {
                  backgroundColor: 'rgba(231, 116, 119, 0.2)',
                  border: '2px solid #e77477',
                },
              }
            }
          />
        }
      </div>
    </div>
  );
}

export default HeaderLinks;
